import { shallowRef, type MaybeRefOrGetter } from "vue";
import { useEventListener, type Arrayable } from "@vueuse/core";

import { OS } from "@/vendor/sML.js";

import {
  isReservedShortcut,
  isSameShortcut,
  normalizeShortcut,
} from "../comm/shortcuts";
import type { Shortcut, ShortcutsRegistration } from "../comm/types";
import eventBus from "../event-bus";

const isApple = !!(OS.macOS || OS.iOS);
const EDITABLE_TAGS = ["TEXTAREA", "SELECT"];
// inputs that don't receive typed text and keep their focus after a click,
// any other type (text, number, date, ...) is considered editable
const NON_TEXT_INPUT_TYPES = [
  "button",
  "checkbox",
  "color",
  "file",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
];

const shortcuts = shallowRef<Required<Shortcut>[]>([]);

/**
 * Filters the embedder's shortcuts. The valid shortcuts are kept to be used.
 * The invalid or reserved shortcuts are rejected and the embedder is notified.
 */
export function registerShortcuts(list: Shortcut[]): ShortcutsRegistration {
  if (!Array.isArray(list)) {
    throw new TypeError("setShortcuts expects an array of shortcuts");
  }

  const accepted: Required<Shortcut>[] = [];
  const rejected: ShortcutsRegistration["rejected"] = [];
  for (const shortcut of list) {
    // alt isn't part of the API but may still be sent by untyped embedders
    const hasAlt = !!(shortcut as { alt?: unknown })?.alt;
    if (typeof shortcut?.key !== "string" || !shortcut.key || hasAlt) {
      rejected.push({ shortcut, reason: "invalid" });
      continue;
    }

    const normalized = normalizeShortcut(shortcut);
    if (isReservedShortcut(normalized)) {
      rejected.push({ shortcut, reason: "reserved" });
    } else if (accepted.some((s) => isSameShortcut(s, normalized))) {
      rejected.push({ shortcut, reason: "duplicate" });
    } else {
      accepted.push(normalized);
    }
  }

  shortcuts.value = accepted;
  return { rejected };
}

function isEditable(target: EventTarget | null): boolean {
  // instanceof can't be used: events may come from iframes (epub)
  const el = target as HTMLElement | null;
  if (!el?.tagName) return false;
  // tagName is only uppercased in HTML documents, epub resources can be XHTML
  const tag = el.tagName.toUpperCase();
  if (el.isContentEditable || EDITABLE_TAGS.includes(tag)) return true;
  return (
    tag === "INPUT" &&
    !NON_TEXT_INPUT_TYPES.includes((el as HTMLInputElement).type)
  );
}

function matches(event: KeyboardEvent): Required<Shortcut> | null {
  const mod = isApple ? event.metaKey : event.ctrlKey;
  const other = isApple ? event.ctrlKey : event.metaKey;
  if (other) return null;

  const pressed = normalizeShortcut({
    key: event.key,
    mod,
    shift: event.shiftKey,
  });
  return shortcuts.value.find((s) => isSameShortcut(s, pressed)) ?? null;
}

function onKeydown(event: KeyboardEvent) {
  if (!shortcuts.value.length || !event.key) return;
  if (event.isComposing || event.altKey) return;
  // the target is retargeted to the host when the field is inside a shadow root
  if (isEditable(event.composedPath()[0] ?? event.target)) return;

  const shortcut = matches(event);
  if (!shortcut) return;

  // repeats must be prevented too, or holding the keys triggers the browser's action
  event.preventDefault();
  if (!event.repeat) eventBus.emit("embed:shortcut", shortcut);
}

/**
 * Listens to the embedder's shortcuts on the given targets (window, iframes' Window, ...).
 */
export function useEmbedShortcuts(
  targets: MaybeRefOrGetter<Arrayable<EventTarget>>
) {
  useEventListener(targets, "keydown", onKeydown, { passive: false });
}
